import React, { useEffect, useState } from 'react';
import { sp } from './sponsorApi';
import { Rocket, Plus, AlertTriangle, ShieldCheck, Loader2, Send } from 'lucide-react';

const PROGRAM_TYPES = [
  { v: 'COFINANCING_ALLOCATED', l: 'Cofinancement affecté (non remboursable)' },
  { v: 'DIRECT_SERVICE', l: 'Prestation directe du sponsor' },
  { v: 'IN_KIND_MATERIAL', l: 'Soutien matériel' },
  { v: 'TECHNICAL_SUPPORT', l: 'Soutien technique' },
  { v: 'LOAN', l: '⚠ Prêt (réglementé)' },
  { v: 'GUARANTEE', l: '⚠ Garantie (réglementé)' },
];
const PAYMENT_MODES = [
  { v: 'DIRECT_TO_SUPPLIER', l: 'Paiement direct au fournisseur' },
  { v: 'DIRECT_TO_SPONSOR', l: 'Paiement direct au sponsor (prestation)' },
  { v: 'NONE', l: 'Sans paiement' },
];
const REQUEST_EVENTS = ['ACCEPTED', 'PAID_DIRECT', 'DELIVERED', 'CLOSED', 'REFUND'];

export default function ProgrammesTab() {
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ title: '', description: '', program_type: 'COFINANCING_ALLOCATED', payment_mode: 'DIRECT_TO_SUPPLIER', amount: '', service_description: '', is_repayable: false, firm_offer: false });
  const [msg, setMsg] = useState('');
  const [openReq, setOpenReq] = useState(null);
  const [reqs, setReqs] = useState([]);

  const load = async () => {
    setLoading(true);
    try { const r = await sp.get('/programmes'); setProgrammes(r.data.programmes || []); }
    catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setMsg('');
    try {
      const r = await sp.post('/programmes', {
        title: f.title, description: f.description, program_type: f.program_type,
        payment_mode: f.payment_mode, amount: parseFloat(f.amount) || null,
        service_description: f.service_description, is_repayable: f.is_repayable, firm_offer: f.firm_offer,
      });
      setMsg(r.data.message);
      setF({ title: '', description: '', program_type: 'COFINANCING_ALLOCATED', payment_mode: 'DIRECT_TO_SUPPLIER', amount: '', service_description: '', is_repayable: false, firm_offer: false });
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Échec.'); }
  };

  const publish = async (id) => {
    setMsg('');
    try { await sp.post(`/programmes/${id}/publier`, {}); await load(); }
    catch (e) { setMsg(e?.response?.data?.detail || 'Publication impossible.'); }
  };

  const loadReqs = async (id) => {
    setOpenReq(id);
    const r = await sp.get(`/programmes/${id}/demandes`); setReqs(r.data.demandes || []);
  };
  const issueOffer = async (rid) => {
    const amount = parseFloat(window.prompt('Montant de l\'offre ferme (€) :') || '0');
    const details = window.prompt('Détails de l\'offre :') || '';
    if (!details) return;
    await sp.post(`/programmes/demandes/${rid}/offre`, { offer_amount: amount, offer_details: details });
    await loadReqs(openReq);
  };
  const advance = async (rid, event) => { await sp.post(`/programmes/demandes/${rid}/statut/${event}`, {}); await loadReqs(openReq); };

  if (loading) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>;

  return (
    <div className="space-y-8" data-testid="programmes-tab">
      <div className="flex items-start gap-3 text-slate-300 bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 text-sm">
        <ShieldCheck className="w-5 h-5 text-amber-400 mt-0.5" />
        <p>Garde-fous : l'éligibilité est <b>gratuite</b>, une <b>offre ferme</b> précède tout paiement, les fonds sont payés <b>directement</b> (jamais via O'SCOP). Les dispositifs de crédit / garantie / assurance sont <b>bloqués</b> jusqu'à validation juridique O'SCOP.</p>
      </div>

      <section className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-2 text-emerald-400 mb-4"><Rocket className="w-5 h-5" /><h2 className="text-white font-semibold">Nouveau programme de soutien</h2></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Titre" data-testid="prog-title-input" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          <input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Montant € (optionnel)" data-testid="prog-amount-input" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          <select value={f.program_type} onChange={(e) => setF({ ...f, program_type: e.target.value })} data-testid="prog-type-select" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm">
            {PROGRAM_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <select value={f.payment_mode} onChange={(e) => setF({ ...f, payment_mode: e.target.value })} data-testid="prog-payment-select" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm">
            {PAYMENT_MODES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" data-testid="prog-desc-input" className="md:col-span-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" rows={2} />
          {f.payment_mode === 'DIRECT_TO_SPONSOR' && (
            <input value={f.service_description} onChange={(e) => setF({ ...f, service_description: e.target.value })} placeholder="Prestation précise du sponsor (obligatoire)" data-testid="prog-service-input" className="md:col-span-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
          )}
          <label className="flex items-center gap-2 text-slate-300 text-sm"><input type="checkbox" checked={f.firm_offer} onChange={(e) => setF({ ...f, firm_offer: e.target.checked })} data-testid="prog-firmoffer-check" className="accent-emerald-500" /> Offre ferme disponible</label>
          <label className="flex items-center gap-2 text-slate-300 text-sm"><input type="checkbox" checked={f.is_repayable} onChange={(e) => setF({ ...f, is_repayable: e.target.checked })} data-testid="prog-repayable-check" className="accent-amber-500" /> Remboursable (déclenche blocage)</label>
        </div>
        <button onClick={create} disabled={!f.title} data-testid="prog-create-btn" className="mt-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-1"><Plus className="w-4 h-4" />Créer</button>
        {msg && <p className="text-sm text-amber-300 mt-2" data-testid="prog-msg">{msg}</p>}
      </section>

      <section className="space-y-3">
        {programmes.length === 0 && <p className="text-slate-500 text-sm" data-testid="prog-empty">Aucun programme.</p>}
        {programmes.map((p) => (
          <div key={p.id} className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid={`prog-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white font-medium">{p.title}</p>
                <p className="text-slate-500 text-xs mt-0.5">{PROGRAM_TYPES.find((t) => t.v === p.program_type)?.l} · {p.amount ? `${p.amount} €` : 'montant libre'}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded ${p.status === 'PUBLISHED' ? 'bg-emerald-600/30 text-emerald-300' : p.regulatory_blocked ? 'bg-red-600/30 text-red-300' : 'bg-slate-700 text-slate-300'}`} data-testid={`prog-status-${p.id}`}>{p.status}</span>
            </div>
            {p.regulatory_blocked && (
              <div className="mt-3 flex items-start gap-2 text-red-300 bg-red-500/10 rounded-lg p-3 text-xs" data-testid={`prog-blocked-${p.id}`}>
                <AlertTriangle className="w-4 h-4 mt-0.5" /><span>{p.block_reason}</span>
              </div>
            )}
            <div className="mt-3 flex items-center gap-3">
              {p.status !== 'PUBLISHED' && (() => {
                const blocked = p.regulatory_blocked && !p.legal_validated;
                const canPublish = !blocked && p.firm_offer;
                return (
                  <button onClick={() => publish(p.id)} disabled={!canPublish} data-testid={`prog-publish-${p.id}`}
                    title={blocked ? 'Validation juridique O\'SCOP requise' : (!p.firm_offer ? 'Offre ferme requise avant publication' : '')}
                    className={`text-sm ${canPublish ? 'text-emerald-400 hover:text-emerald-300' : 'text-slate-600 cursor-not-allowed'}`}>
                    Publier{!canPublish && ' 🔒'}
                  </button>
                );
              })()}
              <button onClick={() => loadReqs(p.id)} data-testid={`prog-requests-${p.id}`} className="text-slate-300 hover:text-white text-sm">Demandes d'éligibilité</button>
            </div>
            {openReq === p.id && (
              <div className="mt-4 border-t border-slate-800 pt-3" data-testid={`prog-reqlist-${p.id}`}>
                {reqs.length === 0 && <p className="text-slate-500 text-sm">Aucune demande.</p>}
                {reqs.map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-sm py-1.5" data-testid={`req-${r.id}`}>
                    <span className="text-slate-200">{r.requester_name} · {r.requester_email} <span className="text-slate-500 text-xs">({r.status})</span></span>
                    <div className="flex items-center gap-2">
                      {r.status === 'ELIGIBILITY_REQUESTED' && <button onClick={() => issueOffer(r.id)} data-testid={`req-offer-${r.id}`} className="text-emerald-400 text-xs flex items-center gap-1"><Send className="w-3 h-3" />Offre ferme</button>}
                      <select onChange={(e) => e.target.value && advance(r.id, e.target.value)} defaultValue="" data-testid={`req-advance-${r.id}`} className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200">
                        <option value="">Faire évoluer…</option>
                        {REQUEST_EVENTS.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
